import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Truck, ShieldCheck, Heart } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { Stars } from "../components/ProductCard";
import { BuyButtonLarge } from "../components/BuyButton";
import { ShareButtons } from "../components/ShareButtons";
import { SafeImage } from "../components/SafeImage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const ZoomImage = ({ src, alt }) => {
    const ref = useRef(null);
    const imgRef = useRef(null);
    const [zoom, setZoom] = useState(false);
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [src]);

    const onMove = (e) => {
        const el = ref.current;
        const img = imgRef.current;
        if (!el || !img) return;
        const rect = el.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
    };

    return (
        <div
            ref={ref}
            onMouseEnter={() => setZoom(true)}
            onMouseLeave={() => setZoom(false)}
            onMouseMove={onMove}
            data-testid="zoom-image"
            className="relative aspect-square bg-brand-bg overflow-hidden rounded-sm cursor-zoom-in select-none"
        >
            {src && !failed ? (
                <img
                    ref={imgRef}
                    src={src}
                    alt={alt}
                    onError={() => setFailed(true)}
                    className={`zoom-img w-full h-full object-cover transition-transform duration-300 ${zoom ? "scale-[2]" : "scale-100"}`}
                    draggable={false}
                />
            ) : (
                <div data-testid="zoom-image-fallback" className="w-full h-full flex items-center justify-center font-heading text-7xl text-brand-gold">
                    {(alt || "V").charAt(0)}
                </div>
            )}
        </div>
    );
};

const ProductDetail = () => {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [active, setActive] = useState(0);
    const [size, setSize] = useState("");
    const [color, setColor] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [fav, setFav] = useState(false);
    const [catName, setCatName] = useState("");

    useEffect(() => {
        setLoading(true);
        setError("");
        api.get(`/products/${slug}`)
            .then(({ data }) => {
                setProduct(data);
                setSize(data.sizes?.[0] || "");
                setColor(data.colors?.[0]?.name || "");
                setActive(0);
                setFav((JSON.parse(localStorage.getItem("vdp_favs") || "[]")).includes(data.slug));
                // Track product view
                api.post("/track", { type: "product_view", product_id: data.id, category_slug: data.category_slug }).catch(() => {});
                api.get("/categories").then(({ data: cats }) => setCatName(cats.find((c) => c.slug === data.category_slug)?.name || "")).catch(() => {});
                return api.get(`/products?category=${data.category_slug}`);
            })
            .then(({ data }) => setRelated(data.filter((p) => p.slug !== slug).slice(0, 4)))
            .catch(() => setError("Produto não encontrado."))
            .finally(() => setLoading(false));
    }, [slug]);

    const toggleFav = () => {
        const favs = JSON.parse(localStorage.getItem("vdp_favs") || "[]");
        const next = fav ? favs.filter((x) => x !== product.slug) : [...favs, product.slug];
        localStorage.setItem("vdp_favs", JSON.stringify(next));
        setFav(!fav);
    };

    const hasDiscount = product?.original_price && product.original_price > product.price;
    const images = product?.images?.length ? product.images : [];

    if (loading) {
        return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-brand-inkSoft" data-testid="detail-loading">Carregando…</div>;
    }
    if (error || !product) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="detail-error">
                <p className="text-brand-inkSoft">{error || "Produto não encontrado."}</p>
                <Link to="/produtos" className="mt-4 inline-block text-brand-gold font-semibold hover:underline">Voltar para produtos</Link>
            </div>
        );
    }

    return (
        <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-brand-inkSoft mb-6" data-testid="detail-breadcrumb">
                <Link to="/" className="hover:text-brand-ink">Início</Link>
                <ChevronRight className="w-3 h-3" />
                <Link to="/produtos" className="hover:text-brand-ink">Produtos</Link>
                <ChevronRight className="w-3 h-3" />
                <Link to={`/produtos?categoria=${product.category_slug}`} className="hover:text-brand-ink capitalize">{catName || product.category_slug}</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-brand-ink font-semibold truncate max-w-[180px]">{product.name}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
                {/* Gallery */}
                <div data-testid="product-gallery">
                    <ZoomImage src={images[active]} alt={product.name} />
                    {images.length > 1 && (
                        <div className="mt-4 grid grid-cols-5 gap-2" data-testid="gallery-thumbs">
                            {images.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActive(i)}
                                    data-testid={`thumb-${i}`}
                                    className={`aspect-square bg-brand-bg rounded-sm overflow-hidden border-2 transition-colors ${active === i ? "border-brand-ink" : "border-transparent hover:border-brand-border"}`}
                                >
                                    <SafeImage src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex flex-col">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-2">{catName || product.category_slug}</div>
                    <h1 data-testid="product-title" className="font-heading font-bold text-3xl sm:text-4xl text-brand-ink leading-tight tracking-tight">
                        {product.name}
                    </h1>
                    <div className="mt-3 flex items-center gap-3">
                        <Stars rating={product.rating} count={product.reviews_count} />
                        <span className="text-xs text-brand-inkSoft">| {product.reviews_count} avaliações</span>
                    </div>

                    <div className="mt-6 flex items-baseline gap-3" data-testid="price-block">
                        <span className="font-heading font-bold text-3xl sm:text-4xl text-brand-ink">{fmtPrice(product.price)}</span>
                        {hasDiscount && <span className="text-base text-brand-inkSoft line-through">{fmtPrice(product.original_price)}</span>}
                        {hasDiscount && (
                            <span className="bg-brand-gold/15 text-brand-goldHover font-bold text-xs uppercase tracking-wider px-2 py-1 rounded-sm">
                                Oferta
                            </span>
                        )}
                    </div>

                    <p className="mt-5 text-brand-inkSoft text-sm sm:text-base leading-relaxed">{product.short_description}</p>

                    {/* Colors */}
                    {product.colors?.length > 0 && (
                        <div className="mt-6" data-testid="colors-block">
                            <div className="text-xs uppercase tracking-[0.15em] font-bold text-brand-ink mb-3">Cor: <span className="font-medium text-brand-inkSoft normal-case tracking-normal">{color}</span></div>
                            <div className="flex gap-2 flex-wrap">
                                {product.colors.map((c) => (
                                    <button
                                        key={c.name}
                                        onClick={() => setColor(c.name)}
                                        data-testid={`color-${c.name}`}
                                        title={c.name}
                                        className={`w-10 h-10 rounded-full border-2 transition-all shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] ${color === c.name ? "border-brand-ink scale-110" : "border-brand-ink/20 hover:border-brand-ink"}`}
                                        style={{ backgroundColor: c.hex }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Sizes */}
                    {product.sizes?.length > 0 && (
                        <div className="mt-6" data-testid="sizes-block">
                            <div className="text-xs uppercase tracking-[0.15em] font-bold text-brand-ink mb-3">Tamanho</div>
                            <div className="flex gap-2 flex-wrap">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSize(s)}
                                        data-testid={`size-${s}`}
                                        className={`min-w-[48px] h-11 px-3 text-sm font-semibold border rounded-sm transition-colors ${
                                            size === s ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border text-brand-ink hover:border-brand-ink"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Stock */}
                    <div className="mt-6 inline-flex items-center gap-2 text-sm text-brand-success font-semibold" data-testid="stock-status">
                        <span className="w-2 h-2 bg-brand-success rounded-full animate-pulse" />
                        {product.stock_status}
                    </div>

                    {/* CTA — botão único ou seletor de plataformas */}
                    <BuyButtonLarge product={product} className="mt-6 w-full" />
                    <p className="mt-3 text-[11px] text-brand-inkSoft text-center">
                        Você será redirecionado para a plataforma escolhida para finalizar a compra.
                    </p>

                    <div className="mt-3 flex gap-3">
                        <button onClick={toggleFav} aria-pressed={fav} className="flex-1 inline-flex items-center justify-center gap-2 border border-brand-border hover:border-brand-ink py-3 text-sm font-semibold text-brand-ink transition-colors rounded-sm" data-testid="favorite-btn">
                            <Heart className={`w-4 h-4 ${fav ? "fill-brand-gold text-brand-gold" : ""}`} /> {fav ? "Favoritado" : "Favoritar"}
                        </button>
                    </div>

                    {/* Compartilhar / Copiar link do produto */}
                    <div className="mt-3" data-testid="share-block">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-brand-inkSoft mb-2">Gostou? Compartilhe</div>
                        <ShareButtons
                            productSlug={product.slug}
                            title={`Olha esse produto que encontrei na Vitrine Deus Proverá: ${product.name}`}
                            testIdPrefix="product-share"
                        />
                    </div>

                    {/* Trust strip */}
                    <div className="mt-8 grid grid-cols-2 gap-3 text-xs">
                        <div className="flex items-start gap-2 p-3 border border-brand-border rounded-sm bg-white">
                            <Truck className="w-4 h-4 text-brand-gold mt-0.5" />
                            <div>
                                <div className="font-bold text-brand-ink">Entrega pela plataforma</div>
                                <div className="text-brand-inkSoft">Cálculo de frete no checkout</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 border border-brand-border rounded-sm bg-white">
                            <ShieldCheck className="w-4 h-4 text-brand-gold mt-0.5" />
                            <div>
                                <div className="font-bold text-brand-ink">Compra protegida</div>
                                <div className="text-brand-inkSoft">100% segura no marketplace</div>
                            </div>
                        </div>
                    </div>

                    {/* Accordion: Detalhes / Envio / Vídeo */}
                    <div className="mt-8" data-testid="detail-accordion">
                        <Accordion type="single" collapsible defaultValue="desc">
                            <AccordionItem value="desc">
                                <AccordionTrigger className="text-sm font-heading font-semibold uppercase tracking-wider">Descrição completa</AccordionTrigger>
                                <AccordionContent className="text-sm text-brand-inkSoft leading-relaxed whitespace-pre-line">
                                    {product.description}
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="ship">
                                <AccordionTrigger className="text-sm font-heading font-semibold uppercase tracking-wider">Envio e entrega</AccordionTrigger>
                                <AccordionContent className="text-sm text-brand-inkSoft leading-relaxed">
                                    O envio é feito diretamente pela plataforma escolhida (Mercado Livre, Shopee, etc.). O prazo e o valor do frete são calculados automaticamente de acordo com o seu CEP no checkout da plataforma.
                                </AccordionContent>
                            </AccordionItem>
                            {product.video_url && (
                                <AccordionItem value="video">
                                    <AccordionTrigger className="text-sm font-heading font-semibold uppercase tracking-wider">Vídeo do produto</AccordionTrigger>
                                    <AccordionContent>
                                        <div className="aspect-video w-full" data-testid="product-video">
                                            {/youtube|vimeo|instagram/i.test(product.video_url) ? (
                                                (() => {
                                                    let src = product.video_url;
                                                    const yt = src.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
                                                    if (yt) src = `https://www.youtube.com/embed/${yt[1]}`;
                                                    const ig = src.match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/);
                                                    if (ig) src = `https://www.instagram.com/${ig[1]}/${ig[2]}/embed`;
                                                    return <iframe className="w-full h-full rounded-sm" src={src} title={product.name} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />;
                                                })()
                                            ) : (
                                                <video src={product.video_url} controls className="w-full h-full rounded-sm bg-black object-contain" />
                                            )}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            )}
                        </Accordion>
                    </div>
                </div>
            </div>

            {/* Related */}
            {related.length > 0 && (
                <section className="mt-20" data-testid="related-section">
                    <div className="flex items-end justify-between mb-8">
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-ink">Você também pode gostar</h2>
                        <Link to={`/produtos?categoria=${product.category_slug}`} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-brand-ink hover:text-brand-gold transition-colors">
                            Ver mais <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {related.map((p) => (
                            <Link key={p.id} to={`/produtos/${p.slug}`} className="group block bg-white border border-brand-border overflow-hidden rounded-sm" data-testid={`related-${p.slug}`}>
                                <div className="aspect-square bg-brand-bg overflow-hidden">
                                    <SafeImage src={p.images?.[0]} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                </div>
                                <div className="p-3">
                                    <div className="font-heading text-sm font-semibold text-brand-ink line-clamp-1">{p.name}</div>
                                    <div className="text-sm text-brand-gold font-bold mt-1">{fmtPrice(p.price)}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default ProductDetail;
